import React, { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { loadViewer } from "./load-viewer";
import type { MountViewer } from "./types";
import styles from "./viewer.module.css";

export function EmbeddedViewer({ options }: { options: Parameters<MountViewer>[1] }) {
  const intl = useIntl();
  const container = useRef<HTMLDivElement>(null);
  const instance = useRef<ReturnType<MountViewer>>();
  const mount = useRef<MountViewer>();
  const latestOptions = useRef(options);
  latestOptions.current = options;
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setFailed(false);
    setLoading(true);
    void loadViewer()
      .then(({ mountViewer }) => {
        if (active && container.current) {
          mount.current = mountViewer;
          instance.current = mountViewer(container.current, latestOptions.current);
          setLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          setFailed(true);
          setLoading(false);
        }
      });
    return () => {
      active = false;
      instance.current?.destroy();
      instance.current = undefined;
      mount.current = undefined;
    };
  }, [attempt]);
  useEffect(() => {
    if (!instance.current) return;
    if (instance.current.update) instance.current.update(options);
    else if (container.current && mount.current) {
      instance.current.destroy();
      instance.current = mount.current(container.current, options);
    }
  }, [options]);
  return (
    <div className={styles["model"]}>
      <div ref={container} className={styles["canvas"]} data-testid="doctor-viewer-model" />
      {loading ? (
        <div role="status" className={styles["loadError"]}>
          {intl.formatMessage({ id: "viewer.loading" })}
        </div>
      ) : null}
      {failed ? (
        <div role="alert" className={styles["loadError"]}>
          <p>{intl.formatMessage({ id: "viewer.load.error" })}</p>
          <button type="button" onClick={() => setAttempt((value) => value + 1)}>
            {intl.formatMessage({ id: "viewer.retry" })}
          </button>
        </div>
      ) : null}
    </div>
  );
}
